import React, { useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import {
  Container, Paper, Typography, Box, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, TextField, MenuItem,
  IconButton, Collapse,
} from '@mui/material'
import CodeIcon from '@mui/icons-material/Code'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { getQorApiLogs } from '../apollo/queries'

const GET_LOGS = gql`${getQorApiLogs}`

const DIRECTION_FILTERS = ['', 'REQUEST', 'WEBHOOK']

function pretty(json) {
  if (!json) return ''
  try { return JSON.stringify(JSON.parse(json), null, 2) } catch (_) { return json }
}

function LogRow({ log }) {
  const [open, setOpen] = useState(false)
  const okColor = log.ok === true ? 'success' : log.ok === false ? 'error' : 'default'
  return (
    <>
      <TableRow hover sx={{ '& td': { borderBottom: open ? 'none' : undefined } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen((o) => !o)}>
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell><Chip size="small" label={log.direction} color={log.direction === 'WEBHOOK' ? 'info' : 'default'} /></TableCell>
        <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{log.method || '—'} {log.path}</Typography></TableCell>
        <TableCell>{log.event || '—'}</TableCell>
        <TableCell>{log.httpStatus ?? '—'}</TableCell>
        <TableCell>{log.gwCode ? `GW${log.gwCode}` : '—'}</TableCell>
        <TableCell><Chip size="small" color={okColor} label={log.status || (log.ok === true ? 'ok' : log.ok === false ? 'fail' : '—')} /></TableCell>
        <TableCell><Typography variant="caption">{log.createdAt ? new Date(log.createdAt).toLocaleString() : '—'}</Typography></TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {log.error && (
                <Box sx={{ flex: '1 1 100%' }}>
                  <Typography variant="caption" color="error" sx={{ fontWeight: 700 }}>Error</Typography>
                  <pre style={{ margin: 0, fontSize: 12, color: '#b91c1c' }}>{log.error}</pre>
                </Box>
              )}
              {log.requestBody && (
                <Box sx={{ flex: '1 1 45%', minWidth: 280 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>Request</Typography>
                  <pre style={{ margin: 0, fontSize: 12, background: '#0f172a', color: '#e2e8f0', padding: 12, borderRadius: 8, overflow: 'auto' }}>{pretty(log.requestBody)}</pre>
                </Box>
              )}
              {log.responseBody && (
                <Box sx={{ flex: '1 1 45%', minWidth: 280 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>{log.direction === 'WEBHOOK' ? 'Webhook payload' : 'Response'}</Typography>
                  <pre style={{ margin: 0, fontSize: 12, background: '#0f172a', color: '#e2e8f0', padding: 12, borderRadius: 8, overflow: 'auto' }}>{pretty(log.responseBody)}</pre>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export default function QorResponses() {
  const [direction, setDirection] = useState('')

  const { data, loading } = useQuery(GET_LOGS, {
    variables: { direction: direction || undefined, limit: 100, offset: 0 },
    fetchPolicy: 'network-only',
    pollInterval: 15000, // refresh so new responses appear as you test
  })

  const rows = data?.qorApiLogs?.rows ?? []
  const count = data?.qorApiLogs?.count ?? 0

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <CodeIcon color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>QorCommerce Responses</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Every QorCommerce interaction — each outbound API request with its raw
          response, and each inbound webhook. Expand a row to see the full
          (sanitized) JSON. Card numbers, tokens and PII are redacted.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            select size="small" label="Type" value={direction}
            onChange={(e) => setDirection(e.target.value)} sx={{ width: 200 }}
          >
            {DIRECTION_FILTERS.map((d) => <MenuItem key={d || 'all'} value={d}>{d || 'All'}</MenuItem>)}
          </TextField>
          <Typography variant="caption" color="text.secondary">{count} total · auto-refreshes</Typography>
        </Box>

        {loading && rows.length === 0 ? <CircularProgress size={24} /> : (
          <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Type</TableCell>
                <TableCell>Endpoint</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>HTTP</TableCell>
                <TableCell>GW</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No responses logged yet.</Typography></TableCell></TableRow>
              )}
              {rows.map((log) => <LogRow key={log._id} log={log} />)}
            </TableBody>
          </Table>
          </Box>
        )}
      </Paper>
    </Container>
  )
}
